import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { WaitingRoomRequest } from 'models/WaitingRoom';
import { DataRoomMemberRole } from '../../models/DataRoomMember';
import { QAndARoles } from '../../models/QAndA';
import httpClient from '../httpClient';

// Sorting, filtering and paging all happen on the BE. Filters ride along as
// their own params next to the standard pagination ones. All but the last are
// multi filters, so each carries a stringified array of the values picked;
// `requestedAt` is one of the windows the table offers (`today`, `7days`, …),
// and is absent when the queue is not being narrowed by age.
export type WaitingRoomParams = PaginationParams & {
  types?: string;
  requestors?: string;
  emails?: string;
  requestingAccessTo?: string;
  requestedAt?: string;
};

function getWaitingRoom(dataRoomId: string, params?: WaitingRoomParams) {
  return httpClient.get<PaginatedData<WaitingRoomRequest>>(
    `data-rooms/${dataRoomId}/waiting-room-requests`,
    { params },
  );
}

// Everything the queue can be filtered by, as the BE resolved it: requester
// names, their emails and the names of what they are asking to get into. The
// keys line up with the filter params above. Type is not in here — its options
// are the request type enum, which the FE already knows.
export type WaitingRoomFilterOptions = {
  requestors: string[];
  emails: string[];
  requestingAccessTo: string[];
};

// The values are drawn from the whole queue, not the page on screen, so this is
// fetched once per data room rather than alongside every list request.
function getWaitingRoomFilterOptions(dataRoomId: string) {
  return httpClient.get<WaitingRoomFilterOptions>(
    `data-rooms/${dataRoomId}/waiting-room-requests/filter-options`,
  );
}

// Everyone waiting, ignoring whatever the table is filtered to. There is no
// dedicated count route: the badge reads `totalItems` off the same list, called
// with no filter params so the total is not narrowed. One row is enough — the
// items are thrown away, only the pagination total is used.
function getWaitingRoomCount(dataRoomId: string) {
  return getWaitingRoom(dataRoomId, { $limit: '1' });
}

// What the seller picks in the review drawer before letting someone in. Both
// roles are chosen up front for the two types that grant entry; handing over a
// password does not, so it sends none.
export type ApproveSecureLinkRequestPayload = {
  role: DataRoomMemberRole;
  qaRole: QAndARoles;
};

// The four routes below act on a single queue row and are addressed by the
// waiting room request id, not by the secure link or the consumer. One decline
// covers every secure link type; approving splits by what the request is asking
// for, because the three outcomes are not interchangeable.
function declineSecureLinkRequest(requestId: string) {
  return httpClient.post(`waiting-room-requests/${requestId}/decline`);
}

// Grants entry through the link the request came in on.
function approveSecureLinkAccess(
  requestId: string,
  data: ApproveSecureLinkRequestPayload,
) {
  return httpClient.post(
    `waiting-room-requests/${requestId}/approve-access`,
    data,
  );
}

// Hands over the password for a link the requester can already reach.
function sendSecureLinkPassword(requestId: string) {
  return httpClient.post(`waiting-room-requests/${requestId}/send-password`);
}

// Pushes out the expiry of a link that lapsed before the requester got in. The
// BE picks the new date.
function extendSecureLinkAccess(
  requestId: string,
  data: ApproveSecureLinkRequestPayload,
) {
  return httpClient.post(
    `waiting-room-requests/${requestId}/extend-access`,
    data,
  );
}

export default {
  getWaitingRoom,
  getWaitingRoomCount,
  getWaitingRoomFilterOptions,
  declineSecureLinkRequest,
  approveSecureLinkAccess,
  sendSecureLinkPassword,
  extendSecureLinkAccess,
};
